import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Snackbar,
  Alert,
  Divider,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Edit as EditIcon,
  PersonAdd as ReassignIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { processApi, taskApi } from '../../../../api/services';
import { bpmnApi } from '../../api/bpmnApi';
import { VariableEditor } from './VariableEditor';
import { TaskReassignDialog } from './TaskReassignDialog';
import type { Task } from '../../../../types/api.types';

interface ProcessControlPanelProps {
  processInstanceId: string;
  onVariablesChanged?: () => void;
}

export function ProcessControlPanel({ processInstanceId, onVariablesChanged }: ProcessControlPanelProps) {
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [editingVariables, setEditingVariables] = useState(false);
  const [reassignTask, setReassignTask] = useState<Task | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const { data: variables, refetch: refetchVariables } = useQuery({
    queryKey: ['process-variables', processInstanceId],
    queryFn: () => processApi.getProcessVariables(processInstanceId),
  });

  const { data: tasks } = useQuery({
    queryKey: ['process-tasks', processInstanceId],
    queryFn: () => taskApi.getTasksByProcess(processInstanceId),
  });

  const updateVariablesMutation = useMutation({
    mutationFn: (newVariables: Record<string, unknown>) =>
      bpmnApi.updateProcessVariables(processInstanceId, newVariables),
    onSuccess: () => {
      refetchVariables();
      queryClient.invalidateQueries({ queryKey: ['process-variables', processInstanceId] });
      setEditingVariables(false);
      setSnackbar({
        open: true,
        message: 'Variablen wurden aktualisiert',
        severity: 'success',
      });
      onVariablesChanged?.();
    },
    onError: () => {
      setSnackbar({
        open: true,
        message: 'Fehler beim Aktualisieren der Variablen',
        severity: 'error',
      });
    },
  });

  const reassignTaskMutation = useMutation({
    mutationFn: ({ taskId, assignee }: { taskId: string; assignee: string }) =>
      bpmnApi.reassignTask(taskId, assignee),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['process-tasks', processInstanceId] });
      setReassignTask(null);
      setSnackbar({
        open: true,
        message: 'Task wurde neu zugewiesen',
        severity: 'success',
      });
    },
    onError: () => {
      setSnackbar({
        open: true,
        message: 'Fehler beim Zuweisen des Tasks',
        severity: 'error',
      });
    },
  });

  const handleSaveVariables = useCallback(
    (newVariables: Record<string, unknown>) => {
      updateVariablesMutation.mutate(newVariables);
    },
    [updateVariablesMutation]
  );

  const handleReassign = useCallback(
    (assignee: string) => {
      if (reassignTask) {
        reassignTaskMutation.mutate({ taskId: reassignTask.id, assignee });
      }
    },
    [reassignTask, reassignTaskMutation]
  );

  const handleCloseSnackbar = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  return (
    <>
      <Card>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
            }}
            onClick={() => setExpanded(!expanded)}
          >
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SettingsIcon />
              Prozess-Steuerung
            </Typography>
            <IconButton size="small">
              <ExpandMoreIcon
                sx={{
                  transform: expanded ? 'rotate(180deg)' : 'none',
                  transition: 'transform 0.2s',
                }}
              />
            </IconButton>
          </Box>

          <Collapse in={expanded}>
            <Box sx={{ mt: 2 }}>
              {/* Variables Section */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle2">Variablen</Typography>
                  {!editingVariables && (
                    <Button
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => setEditingVariables(true)}
                    >
                      Bearbeiten
                    </Button>
                  )}
                </Box>

                {editingVariables && variables ? (
                  <VariableEditor
                    variables={variables}
                    onSave={handleSaveVariables}
                    onCancel={() => setEditingVariables(false)}
                    isSaving={updateVariablesMutation.isPending}
                  />
                ) : (
                  <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1 }}>
                    {variables && Object.keys(variables).length > 0 ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {Object.entries(variables)
                          .filter(([key]) => !['initiator'].includes(key))
                          .slice(0, 5)
                          .map(([key, value]) => (
                            <Chip
                              key={key}
                              label={`${key}: ${String(value).substring(0, 20)}${String(value).length > 20 ? '...' : ''}`}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                        {Object.keys(variables).length > 5 && (
                          <Chip
                            label={`+${Object.keys(variables).length - 5} weitere`}
                            size="small"
                            color="info"
                          />
                        )}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Keine Variablen vorhanden
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Tasks Section */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Tasks verwalten
                </Typography>
                {tasks && tasks.length > 0 ? (
                  <List dense>
                    {tasks.map((task) => (
                      <ListItem key={task.id} sx={{ bgcolor: 'action.hover', borderRadius: 1, mb: 1 }}>
                        <ListItemText
                          primary={task.taskDisplay?.labels['de'] || task.name || task.taskDefinitionKey}
                          secondary={
                            task.assignee ? (
                              <Chip label={task.assignee} size="small" color="success" />
                            ) : (
                              <Chip label="Nicht zugewiesen" size="small" variant="outlined" />
                            )
                          }
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            size="small"
                            onClick={() => setReassignTask(task)}
                            title="Task neu zuweisen"
                          >
                            <ReassignIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Keine aktiven Tasks
                  </Typography>
                )}
              </Box>
            </Box>
          </Collapse>
        </CardContent>
      </Card>

      {/* Task Reassign Dialog */}
      {reassignTask && (
        <TaskReassignDialog
          open={!!reassignTask}
          taskId={reassignTask.id}
          taskName={reassignTask.taskDisplay?.labels['de'] || reassignTask.name || undefined}
          currentAssignee={reassignTask.assignee}
          candidateGroups={reassignTask.candidateGroups}
          onClose={() => setReassignTask(null)}
          onReassign={handleReassign}
          isReassigning={reassignTaskMutation.isPending}
        />
      )}

      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={handleCloseSnackbar}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
